import { useQuery } from '@tanstack/react-query'
import { dashboardApi } from './api'
import type { DashboardOverview } from './api'
import { useAuthStore } from '@/app/store/auth'

// Query keys
export const dashboardKeys = {
    all: ['dashboard'] as const,
    stats: () => [...dashboardKeys.all, 'stats'] as const,
    activity: () => [...dashboardKeys.all, 'activity'] as const,
    metrics: (period: string) => [...dashboardKeys.all, 'metrics', period] as const,
    overview: () => [...dashboardKeys.all, 'overview'] as const,
}

// Dashboard stats
export function useDashboardStats() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    const accessToken = useAuthStore((state) => state.accessToken)

    return useQuery({
        queryKey: dashboardKeys.stats(),
        queryFn: () => dashboardApi.getStats(),
        select: (response) => response.data,
        enabled: isAuthenticated && !mfaRequired && !!accessToken,
        refetchInterval: 30000, // Refetch every 30 seconds
    })
}

// Recent activity
export function useRecentActivity() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    const accessToken = useAuthStore((state) => state.accessToken)

    return useQuery({
        queryKey: dashboardKeys.activity(),
        queryFn: () => dashboardApi.getRecentActivity(),
        select: (response) => response.data,
        enabled: isAuthenticated && !mfaRequired && !!accessToken,
        refetchInterval: 10000, // Refetch every 10 seconds
    })
}

// Dashboard overview (Vault Sentinel stats)
export function useDashboardOverview() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    const accessToken = useAuthStore((state) => state.accessToken)

    return useQuery({
        queryKey: dashboardKeys.overview(),
        queryFn: async () => {
            const response = await dashboardApi.getOverview()
            const bd = response.data as any
            return (bd?.data || bd) as DashboardOverview
        },
        enabled: isAuthenticated && !mfaRequired && !!accessToken,
        refetchInterval: 60000,
    })
}

// Job metrics
export function useJobMetrics(period: '24h' | '7d' | '30d' = '24h') {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)
    const accessToken = useAuthStore((state) => state.accessToken)

    return useQuery({
        queryKey: dashboardKeys.metrics(period),
        queryFn: () => dashboardApi.getJobMetrics(period),
        select: (response) => response.data,
        enabled: isAuthenticated && !mfaRequired && !!accessToken,
        refetchInterval: 60000, // Refetch every minute
    })
}



